<template>
  <div class="relative w-full" ref="searchContainer">
    <div
        class="flex items-center border-grey-200 bg-grey-100 rounded-full border-2 focus-within:border-green-100 pr-1">
      <input
          ref="searchInput"
          type="search"
          class="grow border-none bg-transparent focus:ring-0 text-sm font-light"
          v-model="searchValue"
          placeholder="Rechercher un dossier, une personne..."
          @input="handleInput"
          @focus="showResults = true"
          @keydown.esc="handleEscape"
          @keydown.down="navigateResults('down')"
          @keydown.up="navigateResults('up')"
          @keydown.enter.prevent="selectResult"
      >
      <button
          class="flex items-center justify-center bg-green-100 rounded-full w-8 h-8"
          @click="performSearch"
      >
        <Search class="w-5 h-5"></Search>
      </button>
    </div>

    <!-- Results Dropdown -->
    <Teleport to="body">
      <div
          v-if="showResults && (results.total > 0 || isLoading)"
          :style="dropdownStyle"
          class="fixed z-[9999] bg-white rounded-lg shadow-lg border border-grey-200 max-h-96 overflow-y-auto"
          @mousedown.prevent
      >
      <!-- Loading State -->
      <div v-if="isLoading" class="p-4 text-center text-grey-600">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-green-100 mx-auto"></div>
        <p class="mt-2 text-sm">Recherche en cours...</p>
      </div>

      <!-- Results -->
      <div v-else>
        <!-- Projects -->
        <div v-if="results.projects.length > 0" class="border-b border-grey-200">
          <div class="px-4 py-2 bg-grey-50 text-xs font-semibold text-grey-700 uppercase tracking-wide">
            Dossiers ({{ results.projects.length }})
          </div>
          <div
              v-for="(project, index) in results.projects"
              :key="'project-' + project.id"
              :ref="el => setResultRef(el, 'project', index)"
              class="px-4 py-3 hover:bg-green-50 cursor-pointer transition-colors"
              :class="{ 'bg-green-50': selectedIndex === `project-${index}` }"
              @click="navigateToProject(project.id)"
          >
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <div class="font-medium text-grey-900">{{ project.name }}</div>
                <div class="text-xs text-grey-600 mt-1">
                  <span class="text-gray-500 font-semibold">{{ project.user_initials }}</span> • {{ project.created_at }}
                  <ProjectStatusBadge v-if="project.status" class="ml-3 px-2 py-0.5 bg-grey-200 text-gray-100 rounded text-xs" :status="project.status" :light="true"/>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Legal Entities -->
        <div v-if="results.legal_entities.length > 0" class="border-b border-grey-200">
          <div class="px-4 py-2 bg-grey-50 text-xs font-semibold text-grey-700 uppercase tracking-wide">
            Entités Morales ({{ results.legal_entities.length }})
          </div>
          <div
              v-for="(entity, index) in results.legal_entities"
              :key="'legal-' + entity.id"
              :ref="el => setResultRef(el, 'legal', index)"
              class="px-4 py-3 hover:bg-green-50 cursor-pointer transition-colors"
              :class="{ 'bg-green-50': selectedIndex === `legal-${index}` }"
              @click="navigateToEntity(entity.project_id, entity.id)"
          >
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <div class="font-medium text-grey-900">{{ entity.name }}</div>
                <div class="text-xs text-grey-600 mt-1">
                  SIREN: {{ entity.siren }} • <span class="text-gray-500 font-semibold">{{ entity.project_name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Individual Entities -->
        <div v-if="results.individual_entities.length > 0" class="border-b border-grey-200">
          <div class="px-4 py-2 bg-grey-50 text-xs font-semibold text-grey-700 uppercase tracking-wide">
            Personnes Physiques ({{ results.individual_entities.length }})
          </div>
          <div
              v-for="(entity, index) in results.individual_entities"
              :key="'individual-' + entity.id"
              :ref="el => setResultRef(el, 'individual', index)"
              class="px-4 py-3 hover:bg-green-50 cursor-pointer transition-colors"
              :class="{ 'bg-green-50': selectedIndex === `individual-${index}` }"
              @click="navigateToEntity(entity.project_id, entity.id)"
          >
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <div class="font-medium text-grey-900">{{ entity.name }}</div>
                <div class="text-xs text-grey-600 mt-1">
                  <span class="text-gray-500 font-semibold">{{ entity.project_name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Real Estate Entities -->
        <div v-if="results.real_estate_entities.length > 0">
          <div class="px-4 py-2 bg-grey-50 text-xs font-semibold text-grey-700 uppercase tracking-wide">
            Biens Immobiliers ({{ results.real_estate_entities.length }})
          </div>
          <div
              v-for="(entity, index) in results.real_estate_entities"
              :key="'realestate-' + entity.id"
              :ref="el => setResultRef(el, 'realestate', index)"
              class="px-4 py-3 hover:bg-green-50 cursor-pointer transition-colors"
              :class="{ 'bg-green-50': selectedIndex === `realestate-${index}` }"
              @click="navigateToEntity(entity.project_id, entity.id)"
          >
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <div class="font-medium text-grey-900">{{ entity.name }}</div>
                <div class="text-xs text-grey-600 mt-1">
                  <span v-if="entity.cadastral_reference">Réf: {{ entity.cadastral_reference }} • </span>
                  <span class="text-gray-500 font-semibold">{{ entity.project_name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </Teleport>

    <!-- No Results -->
    <Teleport to="body">
      <div
          v-if="showResults && !isLoading && searchValue && results.total === 0"
          :style="dropdownStyle"
          class="fixed z-[9999] bg-white rounded-lg shadow-lg border border-grey-200 p-4 text-center text-grey-600"
      >
        Aucun résultat trouvé pour "{{ searchValue }}"
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import Search from '@/Components/Client/Icons/Search.vue';
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import { router } from '@inertiajs/vue3';
import axios from 'axios';
import ProjectStatusBadge from "@/Components/Client/Folder/ProjectStatusBadge.vue";

const searchInput = ref(null);
const searchContainer = ref(null);
const searchValue = ref('');
const showResults = ref(false);
const isLoading = ref(false);
const results = ref({
  projects: [],
  legal_entities: [],
  individual_entities: [],
  real_estate_entities: [],
  total: 0
});
const selectedIndex = ref(null);
const resultRefs = ref({});
const dropdownPosition = ref({ top: 0, left: 0, width: 0 });

let searchTimeout = null;

// Calculate dropdown position
const dropdownStyle = computed(() => {
  return {
    top: `${dropdownPosition.value.top}px`,
    left: `${dropdownPosition.value.left}px`,
    width: `${dropdownPosition.value.width}px`,
  };
});

// Update dropdown position
const updateDropdownPosition = () => {
  if (searchContainer.value) {
    const rect = searchContainer.value.getBoundingClientRect();
    dropdownPosition.value = {
      top: rect.bottom + 8, // 8px margin (mt-2)
      left: rect.left,
      width: rect.width,
    };
  }
};

const setResultRef = (el, type, index) => {
  if (el) {
    resultRefs.value[`${type}-${index}`] = el;
  }
};

const handleInput = () => {
  // Debounce search
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  if (searchValue.value.length < 3) {
    results.value = {
      projects: [],
      legal_entities: [],
      individual_entities: [],
      real_estate_entities: [],
      total: 0
    };
    showResults.value = false;
    return;
  }

  searchTimeout = setTimeout(() => {
    performSearch();
  }, 300);
};

const performSearch = async () => {
  if (searchValue.value.length < 3) return;

  isLoading.value = true;
  showResults.value = true;
  selectedIndex.value = null;

  try {
    const response = await axios.get(route('projects.search.meilisearch'), {
      params: { query: searchValue.value }
    });

    results.value = response.data;
  } catch (error) {
    console.error('Search error:', error);
    results.value = {
      projects: [],
      legal_entities: [],
      individual_entities: [],
      real_estate_entities: [],
      total: 0
    };
  } finally {
    isLoading.value = false;
  }
};

const navigateToProject = (projectId) => {
  showResults.value = false;
  searchValue.value = '';
  router.visit(route('projects.view_entities', projectId));
};

const navigateToEntity = (projectId, entityId) => {
  showResults.value = false;
  searchValue.value = '';
  router.visit(route('projects.view_entry', { project: projectId, entity: entityId }));
};

const handleEscape = () => {
  showResults.value = false;
  searchInput.value?.blur();
};

const navigateResults = (direction) => {
  const allResults = [
    ...results.value.projects.map((p, i) => ({ type: 'project', index: i, data: p })),
    ...results.value.legal_entities.map((e, i) => ({ type: 'legal', index: i, data: e })),
    ...results.value.individual_entities.map((e, i) => ({ type: 'individual', index: i, data: e })),
    ...(results.value.real_estate_entities || []).map((e, i) => ({ type: 'realestate', index: i, data: e }))
  ];

  if (allResults.length === 0) return;

  const currentIndex = allResults.findIndex(r => `${r.type}-${r.index}` === selectedIndex.value);

  let nextIndex;
  if (direction === 'down') {
    nextIndex = currentIndex < allResults.length - 1 ? currentIndex + 1 : 0;
  } else {
    nextIndex = currentIndex > 0 ? currentIndex - 1 : allResults.length - 1;
  }

  const nextResult = allResults[nextIndex];
  selectedIndex.value = `${nextResult.type}-${nextResult.index}`;

  // Scroll to selected element
  const element = resultRefs.value[selectedIndex.value];
  if (element) {
    element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
};

const selectResult = () => {
  if (!selectedIndex.value) {
    // If no result is selected, select the first one
    if (results.value.projects.length > 0) {
      navigateToProject(results.value.projects[0].id);
    } else if (results.value.legal_entities.length > 0) {
      const entity = results.value.legal_entities[0];
      navigateToEntity(entity.project_id, entity.id);
    } else if (results.value.individual_entities.length > 0) {
      const entity = results.value.individual_entities[0];
      navigateToEntity(entity.project_id, entity.id);
    } else if (results.value.real_estate_entities?.length > 0) {
      const entity = results.value.real_estate_entities[0];
      navigateToEntity(entity.project_id, entity.id);
    }
    return;
  }

  const [type, index] = selectedIndex.value.split('-');
  const resultsList = type === 'project' ? results.value.projects :
                      type === 'legal' ? results.value.legal_entities :
                      type === 'realestate' ? results.value.real_estate_entities :
                      results.value.individual_entities;

  const result = resultsList[parseInt(index)];
  if (result) {
    if (type === 'project') {
      navigateToProject(result.id);
    } else {
      navigateToEntity(result.project_id, result.id);
    }
  }
};

// Close results when clicking outside
const handleClickOutside = (event) => {
  const target = event.target;
  // Check if click is outside the search container and dropdown
  if (searchContainer.value && !searchContainer.value.contains(target)) {
    // Check if click is not in the teleported dropdown
    const dropdowns = document.querySelectorAll('.fixed.z-\\[9999\\]');
    let clickedInDropdown = false;
    dropdowns.forEach(dropdown => {
      if (dropdown.contains(target)) {
        clickedInDropdown = true;
      }
    });
    if (!clickedInDropdown) {
      showResults.value = false;
    }
  }
};

// Update position when results are shown
watch(() => showResults.value, (newVal) => {
  if (newVal) {
    updateDropdownPosition();
  } else {
    selectedIndex.value = null;
  }
});

// Lifecycle hooks
onMounted(() => {
  if (typeof window !== 'undefined') {
    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
  }
});

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    document.removeEventListener('click', handleClickOutside);
    window.removeEventListener('scroll', updateDropdownPosition, true);
    window.removeEventListener('resize', updateDropdownPosition);
  }
});
</script>